import { Mail, MessageCircle, Phone, User, FileText } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';


const CONTACT_OPTIONS = [
  { id: 'telegram', label: 'Telegram', icon: MessageCircle },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'phone', label: 'Телефон', icon: Phone },
];


export default function BookingContactFields({
  value,
  onChange,
  palette = {},
  emailReadOnly = false,
}) {
  const colors = {
    text: palette.text || 'var(--app-text)',
    secondary: palette.secondary || 'var(--app-text-secondary)',
    muted: palette.muted || 'var(--app-text-muted)',
    faint: palette.faint || 'var(--app-text-faint)',
    input: palette.input || 'var(--app-input-bg)',
    surface: palette.surface || 'var(--app-card-deep)',
    border: palette.border || 'var(--app-border)',
    accent: palette.accent || 'var(--app-accent)',
    accentMuted: palette.accentMuted || 'var(--app-accent-muted)',
  };
  const update = (field, nextValue) => onChange({ ...value, [field]: nextValue });
  const contactPlaceholder = value.contactMethod === 'telegram' ? '@username' : '+7 (999) 123-45-67';
  const contactLabel = value.contactMethod === 'telegram' ? 'Ваш Telegram' : 'Ваш телефон';

  return (
    <div className="space-y-5">
      <div>
        <label htmlFor="booking-patient-name" className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: colors.secondary }}>
          <User size={14} /> Как к вам обращаться <span aria-hidden="true" className="text-red-400">*</span>
        </label>
        <Input
          id="booking-patient-name"
          data-testid="patient-name-input"
          value={value.name}
          onChange={(event) => update('name', event.target.value)}
          placeholder="Имя или псевдоним"
          autoComplete="name"
          required
          style={{ background: colors.input, borderColor: colors.border, color: colors.text }}
        />
        <p className="mt-1.5 text-xs" style={{ color: colors.faint }}>Можно указать имя или псевдоним.</p>
      </div>

      <div>
        <label htmlFor="booking-service-email" className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: colors.secondary }}>
          <Mail size={14} /> Email для сообщений ProPraxis <span aria-hidden="true" className="text-red-400">*</span>
        </label>
        <Input
          id="booking-service-email"
          data-testid="patient-email-input"
          type="email"
          value={value.email}
          onChange={(event) => update('email', event.target.value)}
          placeholder="email@example.com"
          autoComplete="email"
          readOnly={emailReadOnly}
          required
          style={{ background: colors.input, borderColor: colors.border, color: colors.text }}
        />
        <p className="mt-1.5 text-xs leading-relaxed" style={{ color: colors.muted }}>
          Адрес нужен сервису для уведомлений и, где требуется, подтверждения записи. Специалист увидит его только если вы выберете email для связи.
        </p>
      </div>

      <fieldset>
        <legend className="text-sm font-medium mb-2" style={{ color: colors.secondary }}>
          Как специалисту с вами связаться? <span aria-hidden="true" className="text-red-400">*</span>
        </legend>
        <div role="radiogroup" aria-label="Способ связи" className="grid grid-cols-3 gap-2">
          {CONTACT_OPTIONS.map(({ id, label, icon: Icon }) => {
            const selected = value.contactMethod === id;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={selected}
                data-testid={`contact-method-${id}`}
                onClick={() => onChange({ ...value, contactMethod: id, contactValue: '' })}
                className="min-h-11 rounded-lg border px-2.5 py-2 text-xs font-medium flex items-center justify-center gap-1.5 transition-opacity hover:opacity-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                style={{
                  color: selected ? colors.accent : colors.secondary,
                  background: selected ? colors.accentMuted : colors.surface,
                  borderColor: selected ? colors.accent : colors.border,
                  outlineColor: colors.accent,
                }}
              >
                <Icon size={14} /> {label}
              </button>
            );
          })}
        </div>
        {!value.contactMethod && <p className="mt-1.5 text-xs" style={{ color: colors.faint }}>Выберите один удобный способ.</p>}
      </fieldset>

      {(value.contactMethod === 'telegram' || value.contactMethod === 'phone') && (
        <div>
          <label htmlFor="booking-contact-value" className="text-sm font-medium mb-2 block" style={{ color: colors.secondary }}>
            {contactLabel} <span aria-hidden="true" className="text-red-400">*</span>
          </label>
          <Input
            id="booking-contact-value"
            data-testid="patient-contact-input"
            type={value.contactMethod === 'phone' ? 'tel' : 'text'}
            value={value.contactValue}
            onChange={(event) => update('contactValue', event.target.value)}
            placeholder={contactPlaceholder}
            autoComplete={value.contactMethod === 'phone' ? 'tel' : 'off'}
            required
            style={{ background: colors.input, borderColor: colors.border, color: colors.text }}
          />
        </div>
      )}

      <div>
        <label htmlFor="booking-patient-notes" className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: colors.secondary }}>
          <FileText size={14} /> Комментарий специалисту <span className="font-normal" style={{ color: colors.faint }}>(необязательно)</span>
        </label>
        <Textarea
          id="booking-patient-notes"
          data-testid="patient-notes-input"
          value={value.notes}
          onChange={(event) => update('notes', event.target.value)}
          placeholder="Например, удобный способ первого контакта или важная организационная деталь"
          maxLength={1000}
          style={{ background: colors.input, borderColor: colors.border, color: colors.text }}
        />
      </div>
    </div>
  );
}
